import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItemIcon, 
  ListItemText, 
  ListItemButton,
  Box, 
  useMediaQuery, 
  useTheme,
  Menu,
  MenuItem
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard, 
  Notifications, 
  Settings, 
  ExitToApp,
  Person,
  Business,
  History,
  Email
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { RoutesEnum } from '../../utils/routes';

const Header: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isSuperUser, isAdminUser } = useAuth();
  const { t } = useTranslation();
  const isImmigrationCanada = location.pathname.startsWith('/imigra-pulse-canada');

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const safeNavigate = (path: string) => {
    const guard = (window as any).__wgAccountSettingsGuard as ((next: () => void) => void) | undefined;
    const go = () => navigate(path);
    if (guard) {
      guard(go);
    } else {
      go();
    }
  };

  const handleLogout = () => {
    const guard = (window as any).__wgAccountSettingsGuard as ((next: () => void) => void) | undefined;
    const performLogout = () => {
      logout();
      navigate(t(RoutesEnum.LOGIN.HREF(t)));
    };
    if (guard) {
      guard(performLogout);
    } else {
      performLogout();
    }
  };

  // Profile menu
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileAnchorEl);
  const handleOpenProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };
  const handleCloseProfileMenu = () => setProfileAnchorEl(null);
  const handleGoToAccountSettings = () => {
    handleCloseProfileMenu();
    safeNavigate(t(RoutesEnum.ACCOUNT_SETTINGS.HREF(t)) as string);
  };
  const handleGoToSubscriptionPlan = () => {
    handleCloseProfileMenu();
    safeNavigate(t(RoutesEnum.SUBSCRIPTION_PLAN.HREF(t)) as string);
  };

  const menuItems = [
    { text: t(RoutesEnum.DASHBOARD.LABEL(t)), icon: <Dashboard />, path: t(RoutesEnum.DASHBOARD.HREF(t)) },
    { text: t(RoutesEnum.FEATURES.LABEL(t)), icon: <Settings />, path: t(RoutesEnum.FEATURES.HREF(t)) },
    { text: t(RoutesEnum.NOTIFICATIONS.LABEL(t)), icon: <Notifications />, path: t(RoutesEnum.NOTIFICATIONS.HREF(t)) },
    { text: t(RoutesEnum.NOTIFICATION_HISTORY.LABEL(t)), icon: <History />, path: t(RoutesEnum.NOTIFICATION_HISTORY.HREF(t)) },
    { text: t(RoutesEnum.DASHBOARDS.LABEL(t)), icon: <Dashboard />, path: t(RoutesEnum.DASHBOARDS.HREF(t)) },
  ];

  // Itens extras por nível de acesso
  const adminOrSuperItems = (isSuperUser || isAdminUser)
    ? [{ text: t(RoutesEnum.USER_MANAGEMENT.LABEL(t)), icon: <Person />, path: t(RoutesEnum.USER_MANAGEMENT.HREF(t)) }]
    : [];
  const superOnlyItems = isSuperUser
    ? [
        { text: t(RoutesEnum.CUSTOMER_MANAGEMENT.LABEL(t)), icon: <Business />, path: t(RoutesEnum.CUSTOMER_MANAGEMENT.HREF(t)) },
        { text: t(RoutesEnum.WARNING_EMAIL_BLAST.LABEL(t)), icon: <Email />, path: t(RoutesEnum.WARNING_EMAIL_BLAST.HREF(t)) }
      ]
    : [];

  // Combinar menus baseado nas permissões
  const allMenuItems = [...menuItems, ...adminOrSuperItems, ...superOnlyItems];

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle}>
      <List>
        {allMenuItems.map((item) => (
          <ListItemButton 
            key={item.text}
            onClick={() => safeNavigate(item.path as string)}
            sx={{ 
              '&:hover': { 
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText
              }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
        <ListItemButton onClick={() => safeNavigate(t(RoutesEnum.ACCOUNT_SETTINGS.HREF(t)) as string)}>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary={t('header_profile_account_settings')} />
        </ListItemButton>
        <ListItemButton onClick={() => safeNavigate(t(RoutesEnum.SUBSCRIPTION_PLAN.HREF(t)) as string)}>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary={t('header_profile_subscription_plan')} />
        </ListItemButton>
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary={t('header_profile_logout')} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: theme.palette.primary.main }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography 
            variant="h6"
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none', 
              color: 'inherit',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onClick={() => safeNavigate(t(RoutesEnum.HOME.HREF(t)) as string)}
          >
            WG Tech IT
            {isImmigrationCanada && (
              <>
                {' | '}
                <Box
                  component="span"
                  className="fi fi-ca"
                  role="img"
                  aria-label="Canadá"
                  sx={{ width: 20, height: 14, display: 'inline-block', verticalAlign: 'middle' }}
                />
              </>
            )}
          </Typography>
          
          {!isMobile && (
            <Box sx={{ display: 'flex' }}>
              {allMenuItems.map((item) => (
                <Button 
                  key={item.text} 
                  color="inherit"
                  onClick={() => safeNavigate(item.path as string)}
                  sx={{ mx: 1 }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                color="inherit"
                onClick={handleOpenProfileMenu}
                sx={{ ml: 2 }}
              >
                {t('header_profile_label')}
              </Button>
              <Menu
                anchorEl={profileAnchorEl}
                open={profileMenuOpen}
                onClose={handleCloseProfileMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleGoToAccountSettings}>{t('header_profile_account_settings')}</MenuItem>
                <MenuItem onClick={handleGoToSubscriptionPlan}>{t('header_profile_subscription_plan')}</MenuItem>
                <MenuItem onClick={() => { handleCloseProfileMenu(); handleLogout(); }}>{t('header_profile_logout')}</MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
